<template>
  <div>
    <!-- Header -->
    <Header title="Planning des Shifts">
      <template #icon>
        <span class="material-icons">calendar_month</span>
      </template>
      <template #actions>
        <router-link to="/shifts" class="back-button">
          <span class="material-icons">arrow_back</span>
          Retour à la liste
        </router-link>
      </template>
    </Header>
    
    <div class="shift-calendar-container">

    <div class="content-container">
      <!-- Contrôles du calendrier -->
      <div class="calendar-controls">
        <div class="month-selector">
          <button class="btn-nav" @click="previousMonth">
            <span class="material-icons">chevron_left</span>
          </button>
          <h2>{{ currentMonthName }} {{ currentYear }}</h2>
          <button class="btn-nav" @click="nextMonth">
            <span class="material-icons">chevron_right</span>
          </button>
        </div>
        <div class="view-controls">
          <button 
            class="btn-view" 
            :class="{ active: viewMode === 'month' }" 
            @click="viewMode = 'month'"
          >
            Mois
          </button>
          <button 
            class="btn-view" 
            :class="{ active: viewMode === 'week' }" 
            @click="viewMode = 'week'"
          >
            Semaine
          </button>
        </div>
      </div>

      <!-- Vue du mois -->
      <div v-if="viewMode === 'month'" class="month-view">
        <!-- En-têtes des jours de la semaine -->
        <div class="weekday-headers">
          <div v-for="day in weekDays" :key="day" class="weekday-header">
            {{ day }}
          </div>
        </div>
        
        <!-- Grille du calendrier -->
        <div class="calendar-grid">
          <div 
            v-for="(day, index) in calendarDays" 
            :key="index" 
            class="calendar-day"
            :class="{ 
              'other-month': !day.currentMonth,
              'today': day.isToday,
              'weekend': day.isWeekend
            }"
          >
            <div class="day-header">
              <span class="day-number">{{ day.day }}</span>
            </div>
            <div class="day-content">
              <div 
                v-for="shift in getShiftsForDay(day.date)" 
                :key="shift.id" 
                class="shift-item"
                :style="{ backgroundColor: shift.color + '33', borderLeft: `4px solid ${shift.color}` }"
                @click="openShiftDetails(shift)"
              >
                <span class="shift-time">{{ formatTime(shift.heure_debut) }} - {{ formatTime(shift.heure_fin) }}</span>
                <span class="shift-name">{{ shift.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Vue de la semaine -->
      <div v-else-if="viewMode === 'week'" class="week-view">
        <!-- En-têtes des jours de la semaine -->
        <div class="timeline-header">
          <div class="time-column"></div>
          <div 
            v-for="day in currentWeekDays" 
            :key="day.date" 
            class="day-column-header"
            :class="{ 'today': day.isToday, 'weekend': day.isWeekend }"
          >
            <div class="weekday">{{ day.weekday }}</div>
            <div class="date">{{ day.day }}</div>
          </div>
        </div>
        
        <!-- Grille horaire -->
        <div class="timeline-grid">
          <div class="time-labels">
            <div v-for="hour in hours" :key="hour" class="time-label">
              {{ hour }}:00
            </div>
          </div>
          
          <div class="day-columns">
            <div 
              v-for="day in currentWeekDays" 
              :key="day.date" 
              class="day-column"
              :class="{ 'today': day.isToday, 'weekend': day.isWeekend }"
            >
              <div 
                v-for="hour in hours" 
                :key="hour" 
                class="hour-cell"
              ></div>
              
              <div 
                v-for="shift in getShiftsForDay(day.date)" 
                :key="shift.id" 
                class="week-shift-item"
                :style="{ 
                  backgroundColor: shift.color + '33', 
                  borderLeft: `4px solid ${shift.color}`,
                  top: `${getShiftPosition(shift)}px`,
                  height: `${getShiftHeight(shift)}px`
                }"
                @click="openShiftDetails(shift)"
              >
                <div class="shift-content">
                  <div class="shift-name">{{ shift.name }}</div>
                  <div class="shift-time">{{ formatTime(shift.heure_debut) }} - {{ formatTime(shift.heure_fin) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import api from '@/services/api';
import Header from '@/components/Header.vue';

const router = useRouter();

// États
const shifts = ref([]);
const loading = ref(true);
const viewMode = ref('month');
const currentDate = ref(new Date());

// Jours de la semaine
const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const hours = Array.from({ length: 15 }, (_, i) => i + 6); // 6h à 20h

// Mois et année actuels
const currentMonthName = computed(() => {
  return new Date(currentDate.value).toLocaleString('fr-FR', { month: 'long' });
});

const currentYear = computed(() => {
  return new Date(currentDate.value).getFullYear();
});

// Jours du calendrier pour le mois actuel
const calendarDays = computed(() => {
  const year = currentDate.value.getFullYear();
  const month = currentDate.value.getMonth();
  
  // Premier jour du mois
  const firstDay = new Date(year, month, 1);
  // Dernier jour du mois
  const lastDay = new Date(year, month + 1, 0);
  
  // Jour de la semaine du premier jour (0 = dimanche, 1 = lundi, etc.)
  let firstDayOfWeek = firstDay.getDay();
  // Ajuster pour que la semaine commence le lundi (0 = lundi)
  firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;
  
  const days = [];
  
  // Jours du mois précédent
  const prevMonth = new Date(year, month, 0);
  const prevMonthDays = prevMonth.getDate();
  
  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    const day = prevMonthDays - i;
    const date = new Date(year, month - 1, day);
    days.push({
      day,
      date,
      currentMonth: false,
      isToday: isSameDay(date, new Date()),
      isWeekend: date.getDay() === 0 || date.getDay() === 6
    });
  }
  
  // Jours du mois actuel
  for (let i = 1; i <= lastDay.getDate(); i++) {
    const date = new Date(year, month, i);
    days.push({
      day: i,
      date,
      currentMonth: true,
      isToday: isSameDay(date, new Date()),
      isWeekend: date.getDay() === 0 || date.getDay() === 6
    });
  }
  
  // Jours du mois suivant
  const remainingDays = 42 - days.length; // 6 semaines * 7 jours = 42
  for (let i = 1; i <= remainingDays; i++) {
    const date = new Date(year, month + 1, i);
    days.push({
      day: i,
      date,
      currentMonth: false,
      isToday: isSameDay(date, new Date()),
      isWeekend: date.getDay() === 0 || date.getDay() === 6
    });
  }
  
  return days;
});

// Jours de la semaine actuelle
const currentWeekDays = computed(() => {
  const date = new Date(currentDate.value);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Ajuster si c'est dimanche
  
  const monday = new Date(date.setDate(diff));
  
  const days = [];
  for (let i = 0; i < 7; i++) {
    const currentDay = new Date(monday);
    currentDay.setDate(monday.getDate() + i);
    
    days.push({
      date: currentDay,
      weekday: weekDays[i],
      day: currentDay.getDate(),
      isToday: isSameDay(currentDay, new Date()),
      isWeekend: i === 5 || i === 6
    });
  }
  
  return days;
});

// Fonctions de navigation
function previousMonth() {
  const date = new Date(currentDate.value);
  date.setMonth(date.getMonth() - 1);
  currentDate.value = date;
}

function nextMonth() {
  const date = new Date(currentDate.value);
  date.setMonth(date.getMonth() + 1);
  currentDate.value = date;
}

// Fonctions utilitaires
function isSameDay(date1, date2) {
  return date1.getDate() === date2.getDate() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getFullYear() === date2.getFullYear();
}

function formatTime(timeString) {
  if (!timeString) return '';
  return timeString.substring(0, 5); // "08:00:00" -> "08:00"
}

function getShiftsForDay(date) {
  // Dans une implémentation réelle, cette fonction filtrerait les shifts
  // en fonction de la date et des assignations de shifts
  return shifts.value.filter(shift => {
    // Ici, on simule des shifts pour certains jours
    const dayOfWeek = date.getDay();
    
    // Simuler différents shifts pour différents jours de la semaine
    if (shift.code === 'MAT' && (dayOfWeek === 1 || dayOfWeek === 3 || dayOfWeek === 5)) {
      return true;
    }
    if (shift.code === 'APM' && (dayOfWeek === 2 || dayOfWeek === 4)) {
      return true;
    }
    if (shift.code === 'SOR' && (dayOfWeek === 3 || dayOfWeek === 5)) {
      return true;
    }
    
    return false;
  });
}

function getShiftPosition(shift) {
  // Calculer la position verticale du shift dans la vue semaine
  const startHour = parseInt(shift.heure_debut.split(':')[0]);
  const startMinute = parseInt(shift.heure_debut.split(':')[1]);
  
  // Chaque heure fait 60px de hauteur
  return (startHour - 6) * 60 + (startMinute / 60) * 60;
}

function getShiftHeight(shift) {
  // Calculer la hauteur du shift dans la vue semaine
  const startHour = parseInt(shift.heure_debut.split(':')[0]);
  const startMinute = parseInt(shift.heure_debut.split(':')[1]);
  const endHour = parseInt(shift.heure_fin.split(':')[0]);
  const endMinute = parseInt(shift.heure_fin.split(':')[1]);
  
  const durationHours = endHour - startHour;
  const durationMinutes = endMinute - startMinute;
  
  // Chaque heure fait 60px de hauteur
  return durationHours * 60 + (durationMinutes / 60) * 60;
}

function openShiftDetails(shift) {
  router.push(`/shifts/${shift.id}/details`);
}

// Chargement des données
onMounted(async () => {
  try {
    const response = await api.get('/shifts');
    shifts.value = response.data;
  } catch (error) {
    console.error('Erreur lors du chargement des shifts:', error);
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
/* Import des variables de couleurs OpSolver */
@import '@/assets/css/variables.css';

.shift-calendar-container {
  padding: 0 20px 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.content-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
}

.calendar-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.month-selector {
  display: flex;
  align-items: center;
  gap: 15px;
}

.month-selector h2 {
  margin: 0;
  font-size: 1.5rem;
  text-transform: capitalize;
}

.btn-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: var(--turquoise-pale, #E0F7F4);
  color: var(--turquoise-primary, #30C7B5);
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-nav:hover {
  background-color: var(--turquoise-light, #5DD6C7);
  color: white;
}

.view-controls {
  display: flex;
  gap: 10px;
}

.btn-view {
  padding: 8px 16px;
  border: 1px solid var(--turquoise-primary, #30C7B5);
  background-color: white;
  color: var(--turquoise-primary, #30C7B5);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-view.active {
  background-color: var(--turquoise-primary, #30C7B5);
  color: white;
}

.btn-view:hover:not(.active) {
  background-color: var(--turquoise-pale, #E0F7F4);
}

/* Vue du mois */
.month-view {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.weekday-headers {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  background-color: var(--turquoise-primary, #30C7B5);
  color: white;
}

.weekday-header {
  padding: 10px;
  text-align: center;
  font-weight: 500;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  grid-template-rows: repeat(6, 1fr);
  min-height: 600px;
}

.calendar-day {
  border: 1px solid #e0e0e0;
  min-height: 100px;
  padding: 5px;
  background-color: white;
}

.calendar-day.other-month {
  background-color: #f9f9f9;
  color: #aaa;
}

.calendar-day.today {
  background-color: var(--turquoise-pale, #E0F7F4);
}

.calendar-day.weekend {
  background-color: #f5f5f5;
}

.day-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 5px;
}

.day-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-weight: 500;
}

.today .day-number {
  background-color: var(--turquoise-primary, #30C7B5);
  color: white;
}

.day-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
  max-height: 80px;
}

.shift-item {
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: transform 0.1s;
}

.shift-item:hover {
  transform: translateX(2px);
}

.shift-time {
  font-size: 0.7rem;
  opacity: 0.8;
}

.shift-name {
  font-weight: 500;
}

/* Vue de la semaine */
.week-view {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}

.timeline-header {
  display: grid;
  grid-template-columns: 60px repeat(7, 1fr);
  background-color: var(--turquoise-primary, #30C7B5);
  color: white;
}

.day-column-header {
  padding: 10px;
  text-align: center;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
}

.day-column-header.weekend {
  background-color: rgba(0, 0, 0, 0.1);
}

.day-column-header.today {
  background-color: var(--turquoise-dark, #26A194);
}

.weekday {
  font-weight: 500;
}

.date {
  font-size: 0.9rem;
  opacity: 0.9;
}

.timeline-grid {
  display: flex;
  height: 900px; /* 15 heures * 60px */
}

.time-labels {
  width: 60px;
  background-color: #f5f5f5;
}

.time-label {
  height: 60px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 5px;
  border-bottom: 1px solid #e0e0e0;
  font-size: 0.8rem;
  color: #666;
}

.day-columns {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  flex: 1;
  position: relative;
}

.day-column {
  position: relative;
  border-left: 1px solid #e0e0e0;
}

.day-column.weekend {
  background-color: #f9f9f9;
}

.day-column.today {
  background-color: var(--turquoise-pale, #E0F7F4);
}

.hour-cell {
  height: 60px;
  border-bottom: 1px solid #e0e0e0;
}

.week-shift-item {
  position: absolute;
  left: 0;
  right: 0;
  margin: 0 2px;
  border-radius: 4px;
  padding: 4px;
  overflow: hidden;
  cursor: pointer;
  z-index: 1;
  transition: transform 0.1s;
}

.week-shift-item:hover {
  transform: scale(1.01);
  z-index: 2;
}

.shift-content {
  font-size: 0.8rem;
  overflow: hidden;
}

/* Styles pour le Header */
:deep(.header) {
  position: sticky;
  top: 0;
  z-index: 100;
}

/* Styles pour le bouton de retour dans le Header */
:deep(.back-button) {
  display: flex;
  align-items: center;
  color: var(--turquoise-primary);
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

:deep(.back-button:hover) {
  color: var(--turquoise-deep);
  transform: translateX(-3px);
}

:deep(.back-button .material-icons) {
  margin-right: 5px;
}
</style>
